import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiExclamationCircle as ExclamationIcon } from 'react-icons/hi2';
import { Banner, Dialog, useToast } from '@knack/asterisk-react';

import { type KnackApplication } from '@/types/apps';
import { useDeleteAssetsMutation } from '@/hooks/api/mutations/useDeleteAssetsMutation';
import { getErrorMessage } from '@/utils/errors';
import { AppModalFormButtons } from '@/pages/apps/app-modals/AppModalFormButtons';

interface DeleteAppFormProps {
  handleDialogClose: () => void;
  app: KnackApplication;
}

export function DeleteAssetsForm({ handleDialogClose, app }: DeleteAppFormProps) {
  const [t] = useTranslation();
  const { presentToast } = useToast();
  const { name: appName } = app;
  const { deleteUnusedAssets } = useDeleteAssetsMutation();
  const [errorMessage, setErrorMessage] = useState('');

  function onSubmitHandler() {
    deleteUnusedAssets.mutate(app.id, {
      onSuccess: () => {
        handleDialogClose();
        presentToast({
          title: t('components.delete_assets_modal.delete_success', { appName })
        });
      },
      onError: (error) => {
        const message = getErrorMessage(error, t('components.delete_assets_modal.delete_error'));
        setErrorMessage(message);
      }
    });
  }

  return (
    <div className="flex flex-1 flex-col">
      <Dialog.MainContent>
        <div className="space-y-6">
          <p>{t('components.delete_assets_modal.info')}</p>
          <p>{t('components.delete_assets_modal.info_second')}</p>
          <Banner intent="destructive">{t('components.delete_assets_modal.warning')}</Banner>
        </div>
        {errorMessage && (
          <Banner intent="destructive" icon={ExclamationIcon}>
            <Banner.Message>{errorMessage}</Banner.Message>
          </Banner>
        )}
      </Dialog.MainContent>
      <AppModalFormButtons
        submitButtonI18nKey="components.delete_assets_modal.action"
        submitButtonIntent="destructive"
        onSubmit={() => onSubmitHandler()}
        isLoading={deleteUnusedAssets.isPending}
      />
    </div>
  );
}
